import React from 'react';
import { Autocomplete, MenuItem, styled, TextField } from '@mui/material';

export interface GeosuggestSuggest {
    label: string;
    gmaps: google.maps.GeocoderResult;
    location: { lat: number; lng: number };
}

export interface MuiGeosuggestProps {
    value: { lat: number; lng: number };
    onChange: ((value: string) => void) | null;
    onSuggestSelect?: (suggest: GeosuggestSuggest) => void;
    placeholder?: string;
}

const StyledTextField = styled(TextField)(({ theme }) => {
    const light = theme.palette.mode === 'light';
    const bottomLineColor = light ? 'rgba(0, 0, 0, 0.42)' : 'rgba(255, 255, 255, 0.7)';
    return {
        '& .MuiInput-underline:before': {
            borderBottomColor: bottomLineColor
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottomColor: theme.palette.text.primary
        }
    };
});

interface Prediction {
    description: string;
    place_id: string;
}

export const MuiGeosuggest: React.FC<MuiGeosuggestProps> = ({ value, onSuggestSelect, placeholder }) => {
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState<Prediction[]>([]);
    const autocompleteService = React.useRef<google.maps.places.AutocompleteService | null>(null);
    const geocoder = React.useRef<google.maps.Geocoder | null>(null);

    React.useEffect(() => {
        if (!autocompleteService.current && google?.maps?.places) {
            autocompleteService.current = new google.maps.places.AutocompleteService();
        }
        if (!geocoder.current && google?.maps) {
            geocoder.current = new google.maps.Geocoder();
        }
    }, []);

    React.useEffect(() => {
        if (value && value.lat && value.lng && geocoder.current) {
            const latlng = new google.maps.LatLng(value.lat, value.lng);
            geocoder.current.geocode({ location: latlng }, (results, status) => {
                if (String(status) === 'OK' && results && results.length) {
                    setInputValue(results[0].formatted_address);
                }
            });
        }
    }, [value]);

    const fetchPredictions = React.useCallback((input: string) => {
        if (!input || !autocompleteService.current) {
            setOptions([]);
            return;
        }
        autocompleteService.current.getPlacePredictions({ input }, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
                setOptions(predictions.map(p => ({ description: p.description, place_id: p.place_id })));
            } else {
                setOptions([]);
            }
        });
    }, []);

    const handleInputChange = React.useCallback(
        (_event: React.SyntheticEvent, newInputValue: string) => {
            setInputValue(newInputValue);
            fetchPredictions(newInputValue);
        },
        [fetchPredictions]
    );

    const handleSelect = React.useCallback(
        (_event: React.SyntheticEvent, selected: Prediction | null) => {
            if (!selected || !geocoder.current || !onSuggestSelect) {
                return;
            }
            geocoder.current.geocode({ placeId: selected.place_id }, (results, status) => {
                if (String(status) === 'OK' && results && results.length) {
                    const result = results[0];
                    const location = result.geometry.location;
                    onSuggestSelect({
                        label: result.formatted_address,
                        gmaps: result,
                        location: { lat: location.lat(), lng: location.lng() }
                    });
                }
            });
        },
        [onSuggestSelect]
    );

    const getOptionLabel = React.useCallback(
        (option: string | Prediction) => (typeof option === 'string' ? option : option.description),
        []
    );
    const filterOptions = React.useCallback((x: Prediction[]) => x, []);
    const renderOption = React.useCallback(
        (props: React.HTMLAttributes<HTMLLIElement> & { key: string }, option: Prediction) => (
            <MenuItem {...props} key={option.place_id}>
                {option.description}
            </MenuItem>
        ),
        []
    );
    const renderInput = React.useCallback(
        (params: any) => (
            <StyledTextField {...params} variant="standard" placeholder={placeholder} fullWidth />
        ),
        [placeholder]
    );

    return (
        <Autocomplete
            freeSolo
            options={options}
            getOptionLabel={getOptionLabel}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleSelect}
            filterOptions={filterOptions}
            renderOption={renderOption}
            renderInput={renderInput}
        />
    );
};
